import React, { useState, useEffect, useCallback } from 'react';
import { resolveOrgId } from './utils/authHeaders';
import { apiErrorMessage } from './utils/apiError';
import { formatMoney } from './utils/money';
import { WasteReasonFields } from './components/WasteShared';
import {
  Package, AlertTriangle, TrendingDown, DollarSign,
  Filter, Search, RefreshCw,
  BarChart2, ShoppingCart, Loader2, Check, ClipboardList,
  Barcode,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const money = { format: (value) => formatMoney(value) };   // GBP, the product's currency

const toNumber = (value) => {
  const parsed = Number.parseFloat(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Shared auth header helper. Read the org id from the key login actually sets
// (`current_organization_id`, App.js), falling back to the legacy `activeOrgId`.
// Omit X-Organization-Id when unknown rather than sending an empty string, which
// the backend rejects as "required" (mirrors CostIntelligence useOrgScopedHeaders).
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const orgId =
    localStorage.getItem('current_organization_id') ||
    resolveOrgId() ||
    '';
  return {
    'Authorization': `Bearer ${token}`,
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
  };
};

// ─── Phase 2.1 — Variance helpers ────────────────────────────────────────────

const BAND_STYLES = {
  ok:          { bg: 'bg-green-100',  text: 'text-green-700',  label: 'OK'          },
  watch:       { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Watch'       },
  investigate: { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Investigate' },
};

const BandPill = ({ band }) => {
  const s = BAND_STYLES[band] || { bg: 'bg-gray-100', text: 'text-gray-600', label: band };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
};

// Location selector shared by Variance and Reorder tabs.
const useSiteSelector = () => {
  const [locations, setLocations] = useState([]);
  const [siteId, setSiteId]       = useState(() => localStorage.getItem('current_location_id') || '');

  useEffect(() => {
    fetch(`${API_URL}/api/locations`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(locs => {
        const list = Array.isArray(locs) ? locs : [];
        setLocations(list);
        if (!siteId && list.length > 0) setSiteId(list[0].id);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const SiteSelect = ({ testid }) => (
    <div className="flex items-center gap-3">
      <label className="text-sm font-medium text-gray-700">Location</label>
      <select aria-label="Location"
        value={siteId}
        onChange={(e) => setSiteId(e.target.value)}
        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid={testid}
      >
        {locations.length === 0 && <option value="">Loading…</option>}
        {locations.map(loc => (
          <option key={loc.id} value={loc.id}>{loc.name}</option>
        ))}
      </select>
    </div>
  );

  return { siteId, SiteSelect };
};

// ─── Variance Tab ─────────────────────────────────────────────────────────────

const VarianceTab = () => {
  const { siteId, SiteSelect } = useSiteSelector();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const fetchVariance = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/inventory/variance?site_id=${encodeURIComponent(siteId)}`,
        { headers: getAuthHeaders() },
      );
      if (res.ok) {
        setData(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || res.statusText);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { fetchVariance(); }, [fetchVariance]);

  const summary     = data?.summary     || { ok: 0, watch: 0, investigate: 0 };
  const ingredients = data?.ingredients ? Object.entries(data.ingredients) : [];

  return (
    <div data-testid="variance-tab">
      {/* Site selector */}
      <div className="mb-6">
        <SiteSelect testid="variance-site-select" />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { key: 'ok',          label: 'OK',          cls: 'bg-green-50  text-green-700  border-green-200'  },
          { key: 'watch',       label: 'Watch',       cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
          { key: 'investigate', label: 'Investigate', cls: 'bg-red-50    text-red-700    border-red-200'    },
        ].map(({ key, label, cls }) => (
          <div key={key} className={`border rounded-lg p-4 ${cls}`} data-testid={`variance-summary-${key}`}>
            <p className="text-xs font-medium uppercase tracking-wide">{label}</p>
            <p className="text-3xl font-bold mt-1">{summary[key]}</p>
          </div>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700" data-testid="variance-error">
          {error}
        </div>
      )}

      {!loading && !error && data && ingredients.length === 0 && (
        <div className="text-center text-gray-500 py-12 text-sm" data-testid="variance-empty">
          No variance data for this location yet.
        </div>
      )}

      {!loading && ingredients.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="variance-grid">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="text-left  px-4 py-3">Ingredient</th>
                <th className="text-right px-4 py-3">Theoretical</th>
                <th className="text-right px-4 py-3">Actual</th>
                <th className="text-right px-4 py-3">Variance</th>
                <th className="text-right px-4 py-3">Variance %</th>
                <th className="text-center px-4 py-3">Band</th>
              </tr>
            </thead>
            <tbody>
              {ingredients.map(([ingId, row]) => (
                <tr
                  key={ingId}
                  className="border-t border-gray-100 hover:bg-gray-50"
                  data-testid={`variance-row-${ingId}`}
                >
                  <td className="px-4 py-3">
                    <code className="text-xs text-gray-500">{ingId.slice(0, 8)}…</code>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{parseFloat(row.theoretical).toFixed(3)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{parseFloat(row.actual).toFixed(3)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{parseFloat(row.variance).toFixed(3)}</td>
                  <td className={`px-4 py-3 text-right tabular-nums font-medium ${
                    row.band === 'investigate' ? 'text-red-600'
                    : row.band === 'watch'     ? 'text-yellow-600'
                    : 'text-green-600'
                  }`}>
                    {parseFloat(row.pct).toFixed(1)}%
                  </td>
                  <td className="px-4 py-3 text-center">
                    <BandPill band={row.band} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Reorder Tab ──────────────────────────────────────────────────────────────

const ReorderTab = () => {
  const { siteId, SiteSelect } = useSiteSelector();
  const [signals, setSignals]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [approving, setApproving] = useState({}); // { ingredientId: true }
  const [approved,  setApproved]  = useState({}); // { ingredientId: true }

  const fetchSignals = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/inventory/reorder?site_id=${encodeURIComponent(siteId)}`,
        { headers: getAuthHeaders() },
      );
      if (res.ok) {
        setSignals(await res.json());
        setApproved({});
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || res.statusText);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { fetchSignals(); }, [fetchSignals]);

  const approve = async (signal) => {
    const ingId = signal.ingredient_id;
    setApproving(prev => ({ ...prev, [ingId]: true }));
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/inventory/reorder/${encodeURIComponent(ingId)}/approve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            site_id: siteId,
            suggested_qty: signal.suggested_qty != null ? String(signal.suggested_qty) : undefined,
          }),
        },
      );
      if (res.ok) {
        setApproved(prev => ({ ...prev, [ingId]: true }));
      } else {
        const err = await res.json().catch(() => ({}));
        setError(apiErrorMessage(err.detail, `Could not create purchase order (${res.status})`));
      }
    } catch (e) {
      setError('Network error — could not reach the server.');
    } finally {
      setApproving(prev => { const n = { ...prev }; delete n[ingId]; return n; });
    }
  };

  const SourceBadge = ({ src }) => (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-mono ${
      src === 'model' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
    }`}>
      {src}
    </span>
  );

  return (
    <div data-testid="reorder-tab">
      {/* Site selector + refresh */}
      <div className="flex items-center gap-4 mb-6">
        <SiteSelect testid="reorder-site-select" />
        <button
          onClick={fetchSignals}
          disabled={loading}
          className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          data-testid="reorder-refresh-btn"
        >
          Refresh
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700" data-testid="reorder-error">
          {error}
        </div>
      )}

      {!loading && !error && signals.length === 0 && (
        <div className="text-center text-gray-500 py-12 text-sm" data-testid="reorder-empty">
          No reorder signals — all ingredients are above their reorder points.
        </div>
      )}

      {!loading && signals.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="reorder-list">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="text-left  px-4 py-3">Ingredient</th>
                <th className="text-right px-4 py-3">On Hand</th>
                <th className="text-right px-4 py-3">ROP</th>
                <th className="text-right px-4 py-3">Par Level</th>
                <th className="text-right px-4 py-3">Suggested Qty</th>
                <th className="text-center px-4 py-3">Source</th>
                <th className="text-center px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {signals.map((sig) => {
                const isApproved = !!approved[sig.ingredient_id];
                const isBusy     = !!approving[sig.ingredient_id];
                return (
                  <tr
                    key={sig.ingredient_id}
                    className={`border-t border-gray-100 hover:bg-gray-50 ${isApproved ? 'opacity-60' : ''}`}
                    data-testid={`reorder-row-${sig.ingredient_id}`}
                  >
                    <td className="px-4 py-3">
                      <code className="text-xs text-gray-500">{sig.ingredient_id.slice(0, 8)}…</code>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-red-600 font-semibold">
                      {sig.on_hand != null ? parseFloat(sig.on_hand).toFixed(2) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {sig.rop != null ? parseFloat(sig.rop).toFixed(2) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {sig.par_level != null ? parseFloat(sig.par_level).toFixed(2) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-blue-700">
                      {sig.suggested_qty != null ? parseFloat(sig.suggested_qty).toFixed(2) : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <SourceBadge src={sig.suggested_qty_source} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isApproved ? (
                        <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium">
                          <Check className="w-3.5 h-3.5" /> Draft PO
                        </span>
                      ) : (
                        <button
                          onClick={() => approve(sig)}
                          disabled={isBusy}
                          className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50"
                          data-testid={`reorder-approve-${sig.ingredient_id}`}
                        >
                          {isBusy
                            ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" />
                            : 'Create PO'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Tab bar ──────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'stock',    label: 'Stock',    icon: Package    },
  { id: 'variance', label: 'Variance', icon: BarChart2  },
  { id: 'reorder',  label: 'Reorder',  icon: ShoppingCart },
];

// ─── Main component ───────────────────────────────────────────────────────────

const InventoryManagement = () => {
  const [activeTab, setActiveTab] = useState('stock');

  // Stock tab state
  const [items, setItems]       = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [stockError, setStockError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm]             = useState('');
  const [movementItem, setMovementItem] = useState(null);
  const [movementSaving, setMovementSaving] = useState(false);
  const [movementForm, setMovementForm] = useState({
    movement_type: 'receipt',
    qty: '',
    unit_cost: '',
    delta_qty: '',
    ref: '',
    reason: 'spoiled',
    note: '',
  });

  useEffect(() => {
    fetchStockTabData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStockTabData = async () => {
    setLoading(true);
    setStockError(null);
    try {
      const headers = getAuthHeaders();
      const [stockResponse, ingredientResponse] = await Promise.all([
        fetch(`${API_URL}/api/inventory/stock`, { headers }),
        fetch(`${API_URL}/api/costing/ingredients`, { headers }),
      ]);

      if (!stockResponse.ok) {
        const err = await stockResponse.json().catch(() => ({}));
        throw new Error(err.detail || stockResponse.statusText);
      }

      setInventory(await stockResponse.json());
      setItems(ingredientResponse.ok ? await ingredientResponse.json() : []);
    } catch (error) {
      setStockError(error.message || String(error));
      console.error('Error fetching stock data:', error);
    } finally {
      setLoading(false);
    }
  };

  const ingredientById = new Map(items.map(item => [item.id, item]));

  const getInventoryWithItems = () =>
    inventory.map(inv => {
      const item = ingredientById.get(inv.ingredient_id) || {
        id: inv.ingredient_id,
        name: inv.ingredient_id,
        sku: inv.ingredient_id,
        unit: '',
        category: 'uncategorized',
      };
      const available = toNumber(inv.on_hand_qty);
      const wac = toNumber(inv.wac_value);
      return {
        ...inv,
        id: `${inv.location_id}:${inv.ingredient_id}`,
        item: {
          ...item,
          category: item.category || item.item_type || 'uncategorized',
          sku: inv.sku || item.sku || item.id,
          barcode: inv.barcode || item.barcode || '',
          barcode_valid_gtin: inv.barcode_valid_gtin,
          unit: inv.unit || item.unit || '',
          name: inv.name || item.name || inv.ingredient_id,
        },
        available_quantity: available,
        reserved_quantity: 0,
        total_cost: available * wac,
        location: inv.location_id,
      };
    });

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'good', color: 'text-green-600', days: null };
    const diffDays = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0)   return { status: 'expired',       color: 'text-red-600',    days: diffDays };
    if (diffDays <= 7)  return { status: 'expiring-soon', color: 'text-yellow-600', days: diffDays };
    if (diffDays <= 30) return { status: 'expiring',      color: 'text-orange-600', days: diffDays };
    return { status: 'good', color: 'text-green-600', days: diffDays };
  };

  const getStockLevel = (available, reserved, reorderPoint = null) => {
    const total = available + reserved;
    if (available === 0)           return { level: 'out',    color: 'text-red-600',    bg: 'bg-red-100'    };
    if (reorderPoint != null && available <= reorderPoint) {
      return { level: 'low', color: 'text-orange-600', bg: 'bg-orange-100' };
    }
    if (reorderPoint == null && available <= 5) {
      return { level: 'low', color: 'text-orange-600', bg: 'bg-orange-100' };
    }
    if (total > 0 && available <= total * 0.5) {
      return { level: 'medium', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    }
    return { level: 'good', color: 'text-green-600', bg: 'bg-green-100' };
  };

  const inventoryWithItems = getInventoryWithItems();
  const categoryOptions = Array.from(new Set(
    inventoryWithItems.map(inv => inv.item.category).filter(Boolean),
  )).sort();

  const filteredInventory = inventoryWithItems.filter(inv => {
    if (!inv.item) return false;
    if (selectedCategory !== 'all' && inv.item.category !== selectedCategory) return false;
    if (searchTerm) {
      const needle = searchTerm.toLowerCase();
      const haystack = [
        inv.item.name,
        inv.item.sku,
        inv.item.barcode,
        inv.ingredient_id,
        inv.item.category,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });

  const totalValue    = inventoryWithItems.reduce((sum, inv) => sum + inv.total_cost, 0);
  const lowStockItems = inventoryWithItems.filter(inv => {
    const sl = getStockLevel(inv.available_quantity, inv.reserved_quantity, inv.item.rop);
    return sl.level === 'low' || sl.level === 'out';
  }).length;
  const expiringItems = inventoryWithItems.filter(inv => {
    const es = getExpiryStatus(inv.expiry_date);
    return es.status === 'expired' || es.status === 'expiring-soon';
  }).length;

  const openMovementModal = (inventoryItem) => {
    setMovementItem(inventoryItem);
    setMovementForm({
      movement_type: 'receipt',
      qty: '',
      unit_cost: toNumber(inventoryItem.wac_value) > 0 ? String(toNumber(inventoryItem.wac_value)) : '',
      delta_qty: '',
      ref: '',
      reason: 'spoiled',
      note: '',
    });
  };

  const submitMovement = async () => {
    if (!movementItem) return;
    setMovementSaving(true);
    setStockError(null);
    try {
      const isCount = movementForm.movement_type === 'count_adjustment';
      const body = {
        site_id: movementItem.location_id,
        ingredient_id: movementItem.ingredient_id,
        movement_type: movementForm.movement_type,
        qty: isCount ? '0' : movementForm.qty,
        unit_cost: movementForm.movement_type === 'receipt' ? movementForm.unit_cost : undefined,
        delta_qty: isCount ? movementForm.delta_qty : undefined,
        ref: movementForm.ref || undefined,
        // F5: a reasoned waste is costed server-side (WasteRecord + ledger row).
        ...(movementForm.movement_type === 'waste' ? {
          reason: movementForm.reason,
          note: movementForm.note.trim() || undefined,
          uom: movementItem.item.unit || undefined,
        } : {}),
      };
      const response = await fetch(`${API_URL}/api/inventory/movements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || response.statusText);
      }
      setMovementItem(null);
      await fetchStockTabData();
    } catch (error) {
      setStockError(error.message || String(error));
    } finally {
      setMovementSaving(false);
    }
  };

  const InventoryCard = ({ inventoryItem }) => {
    const stockLevel = getStockLevel(
      inventoryItem.available_quantity,
      inventoryItem.reserved_quantity,
      inventoryItem.item.rop,
    );
    const expiryStatus = getExpiryStatus(inventoryItem.expiry_date);
    return (
      <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold">{inventoryItem.item.name}</h3>
            <p className="text-sm text-gray-600">{inventoryItem.item.sku}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${stockLevel.bg} ${stockLevel.color}`}>
            {stockLevel.level.toUpperCase()}
          </span>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Available:</span>
              <p className="font-semibold">{inventoryItem.available_quantity} {inventoryItem.item.unit}</p>
            </div>
            <div>
              <span className="text-gray-600">Reserved:</span>
              <p className="font-semibold">{inventoryItem.reserved_quantity} {inventoryItem.item.unit}</p>
            </div>
          </div>
          <div className="text-sm">
            <span className="text-gray-600">Value:</span>
            <p className="font-semibold">{money.format(inventoryItem.total_cost || 0)}</p>
          </div>
          {inventoryItem.expiry_date && (
            <div className="text-sm">
              <span className="text-gray-600">Expiry:</span>
              <p className={`font-semibold ${expiryStatus.color}`}>
                {new Date(inventoryItem.expiry_date).toLocaleDateString()}
                {expiryStatus.days !== null && ` (${expiryStatus.days} days)`}
              </p>
            </div>
          )}
          <div className="text-sm">
            <span className="text-gray-600">Location:</span>
            <p className="font-semibold">{inventoryItem.location}</p>
          </div>
          {inventoryItem.item.barcode && (
            <div className="text-sm" data-testid={`inventory-barcode-${inventoryItem.ingredient_id}`}>
              <span className="text-gray-600">Barcode:</span>
              <p className="font-mono font-semibold flex items-center gap-2">
                <Barcode className="w-4 h-4 text-gray-500" />
                {inventoryItem.item.barcode}
                {inventoryItem.item.barcode_valid_gtin === false && (
                  <span className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-0.5">internal</span>
                )}
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-between items-center mt-4 text-xs text-gray-500">
          <span>WAC {money.format(toNumber(inventoryItem.wac_value))}</span>
          <code>{inventoryItem.ingredient_id.slice(0, 12)}</code>
        </div>
        <button
          type="button"
          onClick={() => openMovementModal(inventoryItem)}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-800"
        >
          <ClipboardList className="w-4 h-4" />
          Record movement
        </button>
      </div>
    );
  };

  const MovementModal = () => {
    if (!movementItem) return null;
    const isCount = movementForm.movement_type === 'count_adjustment';
    const isReceipt = movementForm.movement_type === 'receipt';
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-md p-6">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{movementItem.item.name}</h2>
              <p className="text-sm text-gray-500">{movementItem.location}</p>
            </div>
            <button
              type="button"
              onClick={() => setMovementItem(null)}
              className="text-gray-500 hover:text-gray-800"
            >
              X
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select aria-label="Type"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={movementForm.movement_type}
                onChange={(e) => setMovementForm({ ...movementForm, movement_type: e.target.value })}
              >
                <option value="receipt">Receive</option>
                <option value="waste">Waste</option>
                <option value="count_adjustment">Count adjustment</option>
              </select>
            </div>

            {isCount ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delta quantity</label>
                <input aria-label="Delta quantity"
                  type="number"
                  step="0.001"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={movementForm.delta_qty}
                  onChange={(e) => setMovementForm({ ...movementForm, delta_qty: e.target.value })}
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <input aria-label="Quantity"
                  type="number"
                  min="0"
                  step="0.001"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={movementForm.qty}
                  onChange={(e) => setMovementForm({ ...movementForm, qty: e.target.value })}
                />
              </div>
            )}

            {movementForm.movement_type === 'waste' && (
              <WasteReasonFields
                reason={movementForm.reason}
                note={movementForm.note}
                onChange={(patch) => setMovementForm({ ...movementForm, ...patch })}
              />
            )}

            {isReceipt && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit cost</label>
                <input aria-label="Unit cost"
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={movementForm.unit_cost}
                  onChange={(e) => setMovementForm({ ...movementForm, unit_cost: e.target.value })}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
              <input aria-label="Reference"
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={movementForm.ref}
                onChange={(e) => setMovementForm({ ...movementForm, ref: e.target.value })}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => setMovementItem(null)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitMovement}
              disabled={movementSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {movementSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save movement'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Inventory Management</h1>
        {activeTab === 'stock' && (
          <button
            onClick={fetchStockTabData}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200 mb-6 flex gap-1" data-testid="inventory-tab-bar">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-blue-600'
            }`}
            data-testid={`inventory-tab-${id}`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Stock tab (original view) ── */}
      {activeTab === 'stock' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-full"><Package className="w-6 h-6 text-blue-600" /></div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Items</p>
                  <p className="text-2xl font-semibold">{inventoryWithItems.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-full"><DollarSign className="w-6 h-6 text-green-600" /></div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Value</p>
                  <p className="text-2xl font-semibold">{money.format(totalValue)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-full"><TrendingDown className="w-6 h-6 text-yellow-600" /></div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Low Stock</p>
                  <p className="text-2xl font-semibold">{lowStockItems}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-red-100 rounded-full"><AlertTriangle className="w-6 h-6 text-red-600" /></div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Expiring Soon</p>
                  <p className="text-2xl font-semibold">{expiringItems}</p>
                </div>
              </div>
            </div>
          </div>

          {stockError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700">
              {stockError}
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select aria-label="Category"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categoryOptions.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input aria-label="Search"
                    type="text"
                    className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2"
                    placeholder="Search items, SKU or barcode..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="inventory-retail-search"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center justify-center space-x-2">
                  <Filter className="w-4 h-4" />
                  <span>More Filters</span>
                </button>
              </div>
            </div>
          </div>

          {/* Inventory Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredInventory.map(item => (
              <InventoryCard key={item.id} inventoryItem={item} />
            ))}
          </div>

          {!stockError && filteredInventory.length === 0 && (
            <div className="text-center text-gray-500 py-12 text-sm">
              No stock ledger rows match the current filters.
            </div>
          )}
        </>
      )}

      {/* ── Variance tab ── */}
      {activeTab === 'variance' && <VarianceTab />}

      {/* ── Reorder tab ── */}
      {activeTab === 'reorder' && <ReorderTab />}

      <MovementModal />
    </div>
  );
};

export default InventoryManagement;
